import { z } from 'zod'

/**
 * Request body for POST /v1/auth/terms-acceptance.
 *
 * `method` describes which flow captured the assent:
 *   - 'email'     - user completed email/password sign-up
 *   - 'google'    - user completed Google OAuth sign-in (first time)
 *   - 'reconsent' - user clicked "I agree and continue" on the re-consent modal
 *
 * `clickwrap` is true when the assent came from an affirmative checkbox click
 * (gold-standard assent). Default false (sign-in-wrap only).
 */
export const RecordTermsAcceptanceRequestSchema = z.object({
  method: z.enum(['email', 'google', 'reconsent']),
  clickwrap: z.boolean().optional().default(false),
})

export type RecordTermsAcceptanceRequest = z.infer<typeof RecordTermsAcceptanceRequestSchema>

export const RecordTermsAcceptanceResponseSchema = z.object({
  /** Version of the Terms of Service that was accepted (ISO date). */
  termsVersion: z.string(),
  /** Version of the Privacy Policy that was accepted (ISO date). */
  privacyVersion: z.string(),
  /** Timestamp at which the acceptance was recorded. */
  acceptedAt: z.string().datetime(),
  /** True when this request inserted a new row; false when it was a duplicate
   * for `(user_id, terms_version)` and the existing row was returned. */
  created: z.boolean(),
})

export type RecordTermsAcceptanceResponse = z.infer<typeof RecordTermsAcceptanceResponseSchema>

/**
 * Response for GET /v1/auth/terms-acceptance-status.
 *
 * `needsReconsent` is true when the user has either never accepted the current
 * Terms + Privacy versions, or their most recent acceptance is for an older
 * version. Exempt routes (/auth/*, /terms, /privacy, /api/*) should still
 * render normally when this is true; other routes should be gated behind a
 * blocking re-consent modal.
 */
export const TermsAcceptanceStatusResponseSchema = z.object({
  needsReconsent: z.boolean(),
  currentTermsVersion: z.string(),
  currentPrivacyVersion: z.string(),
  acceptedTermsVersion: z.string().nullable(),
  acceptedPrivacyVersion: z.string().nullable(),
  acceptedAt: z.string().datetime().nullable(),
})

export type TermsAcceptanceStatusResponse = z.infer<typeof TermsAcceptanceStatusResponseSchema>
